import * as Sentry from "@sentry/browser";

import { getIsAddressUpdateRequired } from '@web/account/utils/getIsAddressUpdateRequired';
import { MODAL_SHOWN_TIMES_LIMIT } from '@web/account/billingWarnings/MissingBillingAddressModal/missingBillingAddress.consts';
import { BILLING_ADDRESS_WARNING_KEY } from '@web/account/constants';

import config from '../../config';

const ANGULAR_LOADING_BAR_LOADING = 'cfpLoadingBar:loading';
const ANGULAR_LOADING_BAR_LOADED = 'cfpLoadingBar:loaded';
const BOOTSTRAP_REQUESTS_TIMEOUT = 2000;
const READONLY_MODE_BANNER_HEIGHT = 40;
export default class AppBootstrap {
  constructor(
    AccountService,
    Authentication,
    Socket,
    RefreshData,
    Education,
    ModalStack,
    Title,
    AlertService,
    Restangular,
    PersonaCollection,
    NewsfeedCollection,
    TaskCollection,
    EventCollection,
    NotificationCollection,
    SalesService,
    IntercomService,
    PremiumDiscoveryService,
    WindowLocation,
    $state,
    $rootScope,
    $timeout,
    $window,
    $http,
    $q,
    $interval,
    $transitions
  ) {
    'ngInject';

    Object.assign(this, {
      AccountService,
      Authentication,
      Socket,
      RefreshData,
      Education,
      ModalStack,
      Title,
      AlertService,
      Restangular,
      PersonaCollection,
      NewsfeedCollection,
      TaskCollection,
      EventCollection,
      NotificationCollection,
      SalesService,
      IntercomService,
      PremiumDiscoveryService,
      WindowLocation,
      $state,
      $rootScope,
      $timeout,
      $window,
      $http,
      $q,
      $interval,
      $transitions,
    });
  }

  async bootstrap() {
    if (this.bootstrapped) {
      return;
    }

    this.handleInitialRequests();
    await this.Authentication.userLoaded;

    Sentry.setUser({
      id: this.Authentication.user.id,
      orgId: this.Authentication.user.org.id,
    })

    this.bootstrapped = true;
  }

  get readonlyMode() {
    return this.$rootScope.readonlyMode;
  }

  set readonlyMode(value) {
    if (value === Boolean(this.$rootScope.readonlyMode)) {
      this.$rootScope.readonlyMode = value;
      return;
    }

    const prevHeight = this.$rootScope.navbarHeight;
    const height = value
      ? prevHeight + READONLY_MODE_BANNER_HEIGHT
      : prevHeight - READONLY_MODE_BANNER_HEIGHT;

    this.$rootScope.readonlyMode = value;
    this.$rootScope.navbarHeight = height;
  }

  handleInitialRequests() {
    let initialRequestsLoadedResolve;
    this.initialRequestsLoaded = this.$q((resolve) => (initialRequestsLoadedResolve = resolve));

    let timer;
    const listeners = [
      this.$rootScope.$on(ANGULAR_LOADING_BAR_LOADING, () => {
        if (timer) {
          this.$timeout.cancel(timer);
        }
      }),
      this.$rootScope.$on(ANGULAR_LOADING_BAR_LOADED, () => {
        if (timer) {
          this.$timeout.cancel(timer);
        }

        timer = this.$timeout(() => {
          this.$rootScope.bootstrapRequestsLoaded = true;
          initialRequestsLoadedResolve();
          listeners.forEach((deregister) => deregister());
        }, BOOTSTRAP_REQUESTS_TIMEOUT);
      }),
    ];
  }

  // eslint-disable-next-line complexity
  handleOnboarding(transition) {
    if (this.Authentication.user) {
      const { onboarding } = this.Authentication.user.settings.clientFlags;

      if (
        this.Authentication.user.isOrgCreator ||
        this.Authentication.user.settings.onboardingComplete
      ) {
        const shouldShowAdminOnboarding =
          this.Authentication.user.isOrgCreator &&
          // TODO is this really needed?
          !this.$rootScope.checkedOnboarding &&
          onboarding &&
          !onboarding.finished;

        if (shouldShowAdminOnboarding && transition.$to().name !== 'orgSetupOnboarding') {
          return transition.router.stateService.target('orgSetupOnboarding', {
            redirectTo: transition.$to().name,
            redirectParams: transition.params(),
          });
        } else if (!shouldShowAdminOnboarding) {
          this.$rootScope.checkedOnboarding = true;
          this.$rootScope.finishedOnboarding = true;
        }
      } else if (transition.$to().name !== 'onboarding') {
        return transition.router.stateService.target('onboarding');
      }
    }
  }

  refreshOnTimezoneChange() {
    const refDate = new Date();
    this.$rootScope.$watch(
      () => refDate.toString().slice(0, -6),
      (current, prev) => {
        if (current !== prev) {
          this.WindowLocation.href = '/';
        }
      }
    );
  }

  setupWebSockets() {
    this.Socket.onOpen(() => {
      this.$rootScope.socketConnection = true;

      // Login to Cloak, the web sockets server
      if (this.Authentication.token) {
        this.Socket.send({
          command: 'join',
          room: this.Authentication.token,
        });
      }
    });

    this.Socket.onError(() => {
      if (!__TEST__) {
        console.log('SOCKET ERROR!');
      }
      this.$rootScope.socketConnection = false;
      this.RefreshData.setDataDirty();
      this.Socket.reconnect();
    });

    this.Socket.onClose(() => {
      this.$rootScope.socketConnection = false;
      this.RefreshData.setDataDirty();
    });

    let socketReconnectNotificationPromise = null;
    const _delayedSocketReconnectNotification = () => {
      const delay = Math.floor(Math.random() * 120) * 1000;
      return this.$timeout(() => this.$rootScope.$broadcast('socket:reconnect'), delay);
    };

    this.Socket.onReconnect(() => {
      if (!__TEST__) {
        console.info('Socket reconnected');
      }

      if (typeof socketReconnectNotificationPromise === 'function') {
        this.$timeout.cancel(socketReconnectNotificationPromise);
      }

      socketReconnectNotificationPromise = _delayedSocketReconnectNotification();
    });
  }

  setupGlobalTransitionsHooks() {
    this.$transitions.onBefore(
      {
        to: (state) => !state.data || !state.data.redirectToLoginApp,
      },
      // eslint-disable-next-line complexity
      async (transition) => {
        if (!this.bootstrapped) {
          await this.bootstrap();
        }

        const redirectToOnboarding = this.handleOnboarding(transition);
        if (redirectToOnboarding) {
          return redirectToOnboarding;
        }

        const toState = transition.$to();
        const toParams = transition.targetState().params();

        this.$rootScope.loggedOut = toState.data.logsOutOnAccess;
        this.$rootScope.bodyClass = toState.data.bodyClass || '';

        if (this.Authentication.user && toState.data.logsOutOnAccess) {
          this.Authentication.logout(true, true);
          return false;
        }

        if (
          toState.data?.access &&
          !this.Authentication.authorize(toState.data.access, toState, toParams)
        ) {
          console.info('user not authorized?');
          this.WindowLocation.href = config.SLING_LOGIN_URL;
          return false;
        }

        if (transition.$to() !== transition.$from()) {
          this.ModalStack.clear();
          this.Education.hide();
        }

        // we split this into two conditions to avoid unnecessary billingInfo calls.
        const shouldLockOnBillingPage =
          config.TAX_CALCULATIONS_ENABLED &&
          getIsWarningsLimitReached(this.Authentication) &&
          (await _shouldLockUserOnBillingPage(this.AccountService, this.Authentication));

        if (
          // if user is locked on the billing page, we want to show alert when they try to leave
          shouldLockOnBillingPage &&
          transition.$from().name === 'account.overview.billing' &&
          // this condition allow updating params while staing on the billing page
          transition.$to().name !== 'account.overview.billing'
        ) {
          this.$rootScope.$broadcast('billing-address-required-triggered');
          this.AlertService.add('Please update your billing information to keep using Sling');
          return false;
        } else if (
          shouldLockOnBillingPage &&
          transition.$to().name !== 'account.overview.billing'
        ) {
          return transition.router.stateService.target('account.overview.billing');
        }

        if (toState.redirectTo) {
          return transition.router.stateService.target(toState.redirectTo, toParams, {
            location: 'replace',
          });
        }

        return true;
      }
    );

    this.$transitions.onSuccess({}, (transition) => {
      if (this.$rootScope.updateAvailable && this.$state.current?.name) {
        this.WindowLocation.replace(
          this.WindowLocation.origin + this.$state.href(this.$state.current.name)
        );
      }

      this.$rootScope.title = this.Title.getTitle();
      if (!transition.options().custom.keepAlert) {
        this.AlertService.clear();
      }

      return true;
    });

    this.$transitions.onError({}, (error) => {
      if (error.error().redirected) {
        console.info(`State redirected from ${error.$to().name} to ${error.error().detail.name()}`);
      }
    });

    this.$transitions.onError(
      {
        to: 'messages|newsfeed|tasks.**',
      },
      (transition) => {
        if (transition.$from().self.url === '^' && transition.error().detail?.status === 405) {
          this.$state.go('dashboard.home', null, {
            custom: {
              keepAlert: true,
            },
          });
        }
      }
    );

    this.$state.defaultErrorHandler((error) => {
      if (error.detail !== 'redirect') {
        console.warn(error);
      }
    });
  }

  setupCollections() {
    this.$rootScope.$on('event:auth-logout', () => {
      this.PersonaCollection.clearData();
      this.NewsfeedCollection.clearData();
      this.NewsfeedCollection.newPosts = {};
      this.TaskCollection.clearData();
      this.EventCollection.clearData();
      this.NotificationCollection.clearData();
      this.SalesService.clearData();

      this.Authentication.leaveSocketRooms();

      void this.IntercomService.reset().catch((err) => {
        console.warn('[Intercom] reset failed', err);
      });
    });

    this.PersonaCollection.onChange(() => {
      if (this.NewsfeedCollection.allChannels) {
        this.Restangular.restangularizeCollection(
          null,
          this.NewsfeedCollection.allChannels,
          'channels'
        );
      }

      if (this.TaskCollection.allTasks) {
        this.Restangular.restangularizeCollection(null, this.TaskCollection.allTasks, 'tasks');
      }

      if (this.NotificationCollection.allNotifications) {
        this.Restangular.restangularizeCollection(
          null,
          this.NotificationCollection.allNotifications,
          'notifications'
        );
      }
    });
  }

  async setupPremiumDiscoveryService() {
    await this.Authentication.userLoaded;
    this.PremiumDiscoveryService.init();
  }

  setupVersionAndMaintenanceWatch() {
    this.$interval(
      () => this._checkVersionAndMaintenance(),
      config.VERSION_AND_MAINTENANCE_CHECK_INTERVAL
    );
    this._checkVersionAndMaintenance();
  }

  async _checkVersionAndMaintenance() {
    this._checkVersion();
    this._checkChristmas();
    this._checkMaintenance();
  }

  async _checkVersion() {
    const response = await this.$http.get('/version.txt', {
      ignoreLoadingBar: true,
      ignoreGlobalErrorHandler: true,
    });

    const v = response.data.trim();

    if (typeof this._checkVersion.version === 'undefined') {
      this._checkVersion.version = v;
    }

    if (this._checkVersion.version !== v) {
      this.$rootScope.updateAvailable = true;
    }
  }

  _checkChristmas() {
    // eslint-disable-next-line
    if (this.$window._IS_CHRISTMAS_THEME !== this.$window.CHECK_IS_CHRISTMAS_THEME()) {
      this.$rootScope.updateAvailable = true;
    }
  }

  async _checkMaintenance() {
    if (!__STAGING__ || __TEST__ || this.$rootScope.readonlyModeForced) {
      if (!this.$rootScope.readonlyModeForced) {
        const response = await this.$http.get('/maintenance-mode.txt', {
          ignoreLoadingBar: true,
          ignoreGlobalErrorHandler: true,
        });
        const r = response.data.trim();
        this.$rootScope.maintenanceMode = r === 'full';
        this.$rootScope.readonlyMode = r === 'readonly';
      } else {
        this.$rootScope.readonlyMode = true;
      }

      this.$rootScope.navbarHeight = this.$rootScope.readonlyMode ? 100 : 60;
    }
  }
}

function getIsWarningsLimitReached(Authentication) {
  const billingAddressWarningClientFlags =
    Authentication.user.settings.clientFlags[BILLING_ADDRESS_WARNING_KEY];

  return billingAddressWarningClientFlags?.count >= MODAL_SHOWN_TIMES_LIMIT;
}

async function _shouldLockUserOnBillingPage(AccountService, Authentication) {
  const { isToastBilling, isToastSignup, isPremium, premiumTrialStartDate, subscriptionStartDate } =
    Authentication.user.org;

  if (!Authentication.user.isManager) {
    return false;
  }

  const isOnTrial = isPremium && premiumTrialStartDate && !subscriptionStartDate;
  const billingInfo = await AccountService.getBillingInfo({ fromCache: true });

  // Before entitlements, orgs were manually put on business plan with $0 price
  // and they are charged on Toast side - we don't want to lock them out of Sling.
  const isToastBillingWithoutEntitlements =
    !billingInfo.nextChargeAmount && billingInfo.isBusinessSubscription;

  // we're temporarily disabling the condition for all toast signups - they are not able to edit their address
  // and if they chose to pay on Sling they will be locked on the page.
  return (
    !isToastSignup &&
    !isToastBillingWithoutEntitlements &&
    getIsAddressUpdateRequired({
      isManager: Authentication.user.isManager,
      isPremium,
      isToastBilling,
      isOnTrial,
      billingInfo,
    })
  );
}
